import { ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import * as api from 'api/api'
import { retryQueuedMutations } from 'api/client'
import { Project } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { Icon } from 'components/Icon'
import { useToast } from 'components/Toast'
import { tx } from 'lib/i18n'
import { useAsync, useOnlineStatus } from 'lib/hooks'
import { subscribeQueue } from 'lib/offlineQueue'

import { ActiveProjectContext } from '../context/ActiveProjectContext'
import { useRegion } from '../context/RegionContext'
import { Sidebar } from '../organisms/Sidebar'
import { Topbar } from '../organisms/Topbar'

const SIDEBAR_COLLAPSED_KEY = 'jopath.sidebar.collapsed'
const MOBILE_SHELL_QUERY = '(max-width: 768px)'

const useIsMobileShell = () => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_SHELL_QUERY).matches)

  useEffect(() => {
    const media = window.matchMedia(MOBILE_SHELL_QUERY)
    const onChange = () => setIsMobile(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  return isMobile
}

export const AppShell = ({ children }: { children: ReactNode }) => {
  const [activeProject, setActiveProject] = useState<Project | null>(null)
  const online = useOnlineStatus()
  const { locale } = useRegion()
  const { user } = useAuth()
  const { data: settings } = useAsync(api.fetchAppSettings, [])
  const notify = useToast()
  const [pendingCount, setPendingCount] = useState(0)
  const wasOffline = useRef(false)

  useEffect(() => subscribeQueue((queue) => setPendingCount(queue.length)), [])

  // Covers both the "came back online" case and a queue left over from a previous session that's
  // already online by the time this page loads.
  useEffect(() => {
    void retryQueuedMutations((label) => notify(tx(locale, `تمت مزامنة التعديل المعلّق (${label})`, `Synced the pending change (${label})`)))
  }, [locale, notify])

  useEffect(() => {
    if (online && wasOffline.current) {
      void retryQueuedMutations((label) => notify(tx(locale, `تمت مزامنة التعديل المعلّق (${label})`, `Synced the pending change (${label})`)))
    }
    wasOffline.current = !online
  }, [locale, online, notify])

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const isMobile = useIsMobileShell()
  const [mobileOpen, setMobileOpen] = useState(false)
  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()

  const closeDrawer = (restoreFocus = false) => {
    setMobileOpen(false)
    if (restoreFocus) document.getElementById('sidebar-toggle')?.focus()
  }

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!isMobile) setMobileOpen(false)
  }, [isMobile])

  useEffect(() => {
    const node = mainRef.current
    if (!node) return
    if (mobileOpen) node.setAttribute('inert', '')
    else node.removeAttribute('inert')
  }, [mobileOpen])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setMobileOpen(false)
      document.getElementById('sidebar-toggle')?.focus()
    }
    window.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [mobileOpen])

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen((open) => !open)
      return
    }
    setCollapsed((value) => {
      const next = !value
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        // ignore storage errors (private mode, etc.)
      }
      return next
    })
  }

  const sidebarOpen = isMobile ? mobileOpen : !collapsed

  return (
    <ActiveProjectContext.Provider value={{ project: activeProject, setProject: setActiveProject }}>
      <a className="skip-link" href="#main">
        {tx(locale, 'تخطي إلى المحتوى', 'Skip to content')}
      </a>
      <div className="shell">
        {isMobile && mobileOpen ? (
          <button type="button" className="shell__backdrop" aria-label={tx(locale, 'إغلاق القائمة', 'Close the menu')} onClick={() => closeDrawer(true)} />
        ) : null}
        <Sidebar collapsed={!isMobile && collapsed} drawerOpen={isMobile && mobileOpen} offCanvas={isMobile} onDismiss={() => closeDrawer(false)} />
        <div className="shell__main">
          <Topbar onToggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
          {!online ? (
            <div className="offline-banner" role="status">
              <Icon name="alert" size={16} />
              {tx(locale, 'لا يوجد اتصال بالإنترنت', 'You are offline')}
              {pendingCount
                ? tx(
                    locale,
                    ` — ${pendingCount} تعديل بانتظار الاتصال، سيُحفظ تلقائياً عند عودته.`,
                    ` — ${pendingCount} ${pendingCount === 1 ? 'change is' : 'changes are'} waiting for a connection and will be saved when you are back online.`
                  )
                : tx(locale, ' — التعديلات الجديدة ستُحفظ تلقائياً عند عودة الاتصال.', ' — new changes will be saved automatically when you are back online.')}
            </div>
          ) : pendingCount ? (
            <div className="offline-banner" role="status">
              <Icon name="refresh" size={16} />
              {tx(locale, `${pendingCount} تعديل بانتظار المزامنة.`, `${pendingCount} ${pendingCount === 1 ? 'change is' : 'changes are'} waiting to sync.`)}
              <button type="button" className="link-button" onClick={() => retryQueuedMutations((label) => notify(tx(locale, `تمت مزامنة التعديل المعلّق (${label})`, `Synced the pending change (${label})`)))}>
                {tx(locale, 'إعادة المحاولة الآن', 'Retry now')}
              </button>
            </div>
          ) : null}
          {settings?.maintenanceMode ? (
            <div className="offline-banner" role="status">
              <Icon name="settings" size={16} />
              {user?.isAdmin
                ? tx(
                    locale,
                    'وضع الصيانة مُفعّل حالياً — المستخدمون الآخرون لا يستطيعون التعديل. أطفئه من الإعدادات عند الانتهاء.',
                    'Maintenance mode is on — other people cannot make changes. Turn it off in Settings when you are done.'
                  )
                : tx(locale, 'النظام قيد الصيانة حالياً — القراءة فقط، لا يمكن إضافة أو تعديل البيانات مؤقتاً.', 'The system is under maintenance — read only. Changes are paused for now.')}
            </div>
          ) : null}
          <main id="main" ref={mainRef} className="main" tabIndex={-1}>
            {children}
          </main>
        </div>
      </div>
    </ActiveProjectContext.Provider>
  )
}
